import React, { useState } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, TextField, Radio, RadioGroup, FormControlLabel, InputAdornment } from '@mui/material';
import moment from 'moment';
import LocalizedDatePicker from '../../../../custom-widgets/LocalizedDatePicker';
import { DatePicker } from '@mui/x-date-pickers-pro';
import useIsMobile from '../../../../hooks/useIsMobile';

const sx = (theme, isMobile) => ({
    wrap: { mb: theme.spacing(2) },
    formControl: {
        margin: theme.spacing(1),
        minWidth: theme.spacing(30),
        padding: '0px 0px',
        ...(isMobile && { minWidth: 0, maxWidth: '100%' }),
    },
    daySelect: { width: theme.spacing(10), ml: theme.spacing(2) },
    unitSelect: { width: theme.spacing(20), ml: theme.spacing(2) },
    dayGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridGap: theme.spacing(1),
        mt: theme.spacing(1),
    },
    day: {
        display: 'flex',
        width: theme.spacing(6),
        height: theme.spacing(6),
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid',
        borderColor: theme.palette.secondary.light,
        cursor: 'pointer',
    },
    daySelected: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        borderColor: theme.palette.primary.dark,
    },
    topMargin: {
        mt: theme.spacing(1),
    },
    marginR: {
        mr: theme.spacing(2),
    },
    occurrenceTextfield: {
        ml: theme.spacing(2),
        width: theme.spacing(25),
        ...(isMobile && { width: 'auto', flex: 1, minWidth: 0 }),
    },
    formControlLabel: {
        ...(isMobile && { width: '100%', mr: 0 }),
    },
    labelBox: {
        ...(isMobile && { flex: 1, minWidth: 0 }),
    },
});

function SelectWeekdays({ show, days, handleDaysChange, disabled }) {
    if (!show) return null;

    return (
        <Box sx={(theme) => sx(theme).topMargin}>
            <Typography>Repeat on</Typography>
            <Box sx={(theme) => sx(theme).dayGrid}>
                {['Su', 'M', 'T', 'W', 'R', 'F', 'Sa'].map((day, d) => {
                    return (
                        <Box
                            sx={(theme) => ({
                                ...sx(theme).day,
                                ...(days.includes(d) ? sx(theme).daySelected : {}),
                            })}
                            onClick={() => {
                                if (!disabled) handleDaysChange(d);
                            }}
                        >
                            {day}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function SelectMonthDays({ show, daysOfMonth, recurDayOfMonth, disabled, handleDayOfMonthChange }) {
    if (!show) return null;

    return (
        <Select sx={(theme) => sx(theme).topMargin} disabled={disabled} value={recurDayOfMonth} onChange={handleDayOfMonthChange} autoWidth>
            <MenuItem value={0}>Monthly on day #</MenuItem>
            {daysOfMonth.map((day) => (
                <MenuItem key={day} value={day}>
                    {day.toString()}
                </MenuItem>
            ))}
        </Select>
    );
}

const getInitialRadioSelection = (item) => {
    if (item.auto_renew) return 'Auto Renew';
    if (item.recur_end_date) return 'On';
    return 'After';
};

// ! This needs to remain for the other dialogs to work for now
function RecurringItemFields({ item, setItem, disabled = false }) {
    const isMobile = useIsMobile();
    const [radioSelection, setRadioSelection] = useState(getInitialRadioSelection(item));
    const pluralChar = item.customFrequency > 1 || item.length > 1 ? 's' : '';
    const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1);

    const handleRadioSelection = (event) => {
        const { value } = event.target;
        setRadioSelection(value);

        if (value === 'On') {
            setItem({
                ...item,
                numberOccurrences: null,
                auto_renew: false,
            });
        } else if (value === 'After') {
            setItem({
                ...item,
                numberOccurrences: item.numberOccurrences ?? 1,
                recurEndDate: null,
                auto_renew: false,
            });
        } else if (value === 'Auto Renew') {
            setItem({
                ...item,
                numberOccurrences: null,
                auto_renew: true,
                recurEndDate: moment().add(3, 'month'),
            });
        }
    };

    const handleFrequencyChange = (event) => {
        setItem({
            ...item,
            frequency: event.target.value,
            unit: 'day',
            customFrequency: 1,
        });
    };

    const handleCustomFrequencyChange = (event) => {
        setItem({
            ...item,
            customFrequency: Math.max(1, Number(event.target.value)),
            frequency: 0,
        });
    };

    const handleDaysChange = (d) => {
        const days = [...item.days];
        const index = days.indexOf(d);
        if (index > -1) {
            if (days.length > 1) {
                days.splice(index, 1);
            }
        } else {
            days.push(d);
            days.sort();
        }
        setItem({
            ...item,
            days,
        });
    };

    const handleUnitChange = (event) => {
        setItem({
            ...item,
            unit: event.target.value,
        });
    };

    const handleEndDate = (date) => {
        setItem({
            ...item,
            recurEndDate: date,
            numberOccurrences: null,
        });
    };

    const handleNumberRecurrences = (event) => {
        setItem({
            ...item,
            numberOccurrences: Math.max(1, Number(event.target.value)),
        });
    };

    const handleDayOfMonthChange = (event) => {
        setItem({
            ...item,
            recurDayOfMonth: event.target.value,
        });
    };

    return (
        <Box sx={(theme) => sx(theme, isMobile).wrap} display="flex" flexDirection="column">
            <FormControl sx={(theme) => sx(theme, isMobile).formControl}>
                <Select disabled={disabled} value={item.frequency} onChange={handleFrequencyChange}>
                    <MenuItem value={1}>Every Day</MenuItem>
                    <MenuItem value={2}>Every Other Day</MenuItem>
                    <MenuItem value={3}>Every 3 Days</MenuItem>
                    <MenuItem value={7}>Weekly</MenuItem>
                    <MenuItem value={0}>Custom</MenuItem>
                </Select>
            </FormControl>
            {item.frequency === 0 && (
                <FormControl sx={(theme) => sx(theme, isMobile).formControl}>
                    <Box sx={(theme) => sx(theme, isMobile).topMargin}>
                        <Typography>
                            <strong>Custom recurrence</strong>
                        </Typography>
                        <Box sx={(theme) => sx(theme, isMobile).topMargin} display="flex">
                            <Typography display="flex" alignItems="center">
                                Repeat every
                            </Typography>
                            <TextField
                                sx={(theme) => sx(theme, isMobile).daySelect}
                                placeholder="#"
                                type="number"
                                inputProps={{ min: 1, max: 62 }}
                                value={item.customFrequency}
                                onChange={handleCustomFrequencyChange}
                                // wrap
                            />
                            <Select sx={(theme) => sx(theme, isMobile).unitSelect} disabled={disabled} value={item.unit} onChange={handleUnitChange}>
                                <MenuItem value={'day'}>{`day${pluralChar}`}</MenuItem>
                                <MenuItem value={'week'}>{`week${pluralChar}`}</MenuItem>
                                <MenuItem value={'month'}>{`month${pluralChar}`}</MenuItem>
                                <MenuItem value={'year'}>{`year${pluralChar}`}</MenuItem>
                            </Select>
                        </Box>
                    </Box>
                    <SelectWeekdays
                        show={item.unit === 'week'}
                        days={item.days}
                        handleDaysChange={handleDaysChange}
                        disabled={disabled}
                        // wrap
                    />
                    <SelectMonthDays
                        show={item.unit === 'month'}
                        daysOfMonth={daysOfMonth}
                        recurDayOfMonth={item.recurDayOfMonth}
                        handleDayOfMonthChange={handleDayOfMonthChange}
                        disabled={disabled}
                        // wrap
                    />
                </FormControl>
            )}
            <FormControl sx={(theme) => sx(theme, isMobile).formControl}>
                <Box sx={(theme) => sx(theme, isMobile).topMargin}>
                    <Typography>Ends</Typography>
                    <RadioGroup value={radioSelection} onChange={handleRadioSelection}>
                        <FormControlLabel
                            value="On"
                            control={<Radio />}
                            disabled={disabled}
                            sx={(theme) => sx(theme, isMobile).formControlLabel}
                            label={
                                <Box display="flex" alignItems="center" sx={(theme) => sx(theme, isMobile).labelBox}>
                                    <Typography sx={(theme) => sx(theme, isMobile).marginR}>On</Typography>
                                    <LocalizedDatePicker
                                        Picker={DatePicker}
                                        size="small"
                                        inputVariant="outlined"
                                        format="DD MMM YYYY"
                                        value={item.recurEndDate ?? moment(item.recur_end_date)}
                                        onChange={handleEndDate}
                                        disabled={radioSelection !== 'On'}
                                        slotProps={isMobile ? { textField: { sx: (theme) => sx(theme, isMobile).labelBox } } : undefined}
                                        // wrap
                                    />
                                </Box>
                            }
                        />
                        {!item.recur_end_date && (
                            <FormControlLabel
                                value="After"
                                control={<Radio />}
                                disabled={disabled}
                                sx={(theme) => sx(theme).topMargin}
                                label={
                                    <Box display="flex" alignItems="center" sx={(theme) => sx(theme, isMobile).labelBox}>
                                        After
                                        <TextField
                                            placeholder="#"
                                            type="number"
                                            value={item.numberOccurrences || 1}
                                            onChange={handleNumberRecurrences}
                                            sx={(theme) => sx(theme, isMobile).occurrenceTextfield}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">{`occurrence${pluralChar}`}</InputAdornment>,
                                            }}
                                            disabled={radioSelection !== 'After'}
                                            size="small"
                                        />
                                    </Box>
                                }
                            />
                        )}
                        <FormControlLabel
                            value="Auto Renew"
                            control={<Radio />}
                            disabled={disabled}
                            sx={(theme) => ({ ...sx(theme, isMobile).topMargin, ...sx(theme, isMobile).formControlLabel })}
                            label={
                                <Box display="flex" alignItems="center" sx={(theme) => sx(theme, isMobile).labelBox}>
                                    Auto Renew
                                </Box>
                            }
                        />
                    </RadioGroup>
                </Box>
            </FormControl>
        </Box>
    );
}

export default RecurringItemFields;
